import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PageHeader } from "@/components/PageHeader";
import { ProductForm } from "./components/ProductForm";
import { ProductImageUploader } from "./components/ProductImageUploader";
import { useProducts, useProduct } from "./hooks/useProducts";
import { useCategories } from "@/pages/categories/hooks/useCategories";
import type { ProductInput } from "@/types/models";

export default function ProductEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { store, updateProduct, saving } = useProducts();
  const { product, loading: productLoading, error: productError } = useProduct(id);
  const { categories, loading: categoriesLoading } = useCategories();
  const [actionError, setActionError] = useState<string | null>(null);

  const handleSubmit = (input: ProductInput) => {
    setActionError(null);
    updateProduct(input, {
      onCompleted: () => navigate("/products"),
      onError: (msg) => setActionError(msg),
    });
  };

  const loading = categoriesLoading || productLoading;

  if (loading) {
    return (
      <div className="container-elegant page-padding space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full max-w-lg" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="container-elegant page-padding">
        <PageHeader title="Editar produto" />
        <section className="max-w-xl border border-primary-200 dark:border-primary-800 panel-padding">
          <h2 className="text-xl font-light text-primary-900 dark:text-white mb-3">
            Crie sua loja primeiro
          </h2>
          <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
            Você precisa configurar uma loja antes de editar produtos.
          </p>
          <Button asChild>
            <Link to="/store/settings">CONFIGURAR LOJA</Link>
          </Button>
        </section>
      </div>
    );
  }

  if (productError || !product) {
    return (
      <div className="container-elegant page-padding">
        <PageHeader title="Editar produto" />
        <Alert variant="destructive" className="max-w-lg">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>
            {productError?.message ?? "Produto não encontrado."}
          </AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-6" asChild>
          <Link to="/products">Voltar para produtos</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="container-elegant page-padding">
      <PageHeader title="Editar produto" />

      {actionError && (
        <Alert variant="destructive" className="mb-6 max-w-lg">
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <ProductForm
        product={product}
        categories={categories}
        saving={saving}
        onSubmit={handleSubmit}
        onCancel={() => navigate("/products")}
      />

      <ProductImageUploader
        productId={product.id}
        images={product.images ?? []}
      />
    </div>
  );
}
